import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import _, { isString } from 'lodash';
import React, { useContext, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList, CreateCardAccountStackRoutes } from './type';
import { Card } from '../../../../../../packages/utx-access-client/src/react';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { CardAccountTypeSelectFilters, CardCurrencySelectFilters, CardIdentityTypeSelectFilters } from '../../options';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const CreateCardAccountNativeOneScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh } = props;
  const { t } = useTranslation('card-account');
  const { body, textMb, textTitle, textAtmPin, btnView, btn, modal, topHeader } = sxStyles;

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submitData = {
    name: data.name,
    phoneNumber: `${data.phoneCode}${data.phoneNumber}`,
    identityType: data.identityType,
    accountType: data.accountType,
    currency: data.currency,
    assetLink: data.accountType == 'asset-link' && data.assetLink ? { accountId: data.assetLink.id } : undefined,
    individual: data.individual && data?.identityType == 'individual' ? data?.individual : undefined,
    creditAccount: data.accountType == 'fixed-limit' && data.creditAccount ? [data.creditAccount.id] : undefined,
    limit: data.accountType == 'fixed-limit' && data.limit ? data.limit : undefined,
    settlementAccount: data.accountType == 'fixed-limit' && data.settlementAccount ? data.settlementAccount.id : undefined
  };

  const snapPoints = useMemo(() => ['50%', '90%'], []);
  return (
    <View sx={{ width, height, backgroundColor: 'transparent', flex: 1 }}>
      <BottomSheet enablePanDownToClose index={1} snapPoints={snapPoints} backdropComponent={renderBackdrop}>
        <ScrollView sx={{ backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
          <View sx={modal}>
            <View>
              <View sx={topHeader}>
                <BottomSheetHeader
                  title={t('card-account::credit-card-new-account.new-account-confirmation.title--new-account-instruction-confirmation')}
                  onPress={() => navigation.goBack()}
                />
              </View>
              <View sx={body}>
                <TextBody sx={textMb} semiBold>
                  {t('card-account::credit-card-new-account.new-account-confirmation.title--create-new-account-inquiry')}
                </TextBody>
                <View>
                  <TextCaption sx={textTitle} semiBold>
                    {t('card-account::credit-card-new-account.glossary.account-name')}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{data.name}</H6>
                </View>
                <View>
                  <TextCaption sx={textTitle} semiBold>
                    {t('card-account::credit-card-new-account.glossary.mobile-number')}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{`+${data.phoneCode} ${data.phoneNumber}`}</H6>
                </View>
                <View>
                  <TextCaption sx={textTitle} semiBold>
                    {t('card-account::credit-card-new-account.glossary.account-type')}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{CardIdentityTypeSelectFilters(t).find((el) => el.value === data.identityType)?.label}</H6>
                </View>
                <View>
                  <TextCaption sx={textTitle} semiBold>
                    {t('card-account::credit-card-new-account.glossary.card-type')}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{CardAccountTypeSelectFilters(t).find((el) => el.value === data.accountType)?.label}</H6>
                </View>
                <View>
                  <TextCaption sx={textTitle} semiBold>
                    {t('card-account::credit-card-new-account.glossary.currency')}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{CardCurrencySelectFilters(t).find((el) => el.value === data.currency)?.label}</H6>
                </View>
                {data.accountType == 'asset-link' && data.assetLink && (
                  <View>
                    <TextCaption sx={textTitle} semiBold>
                      {t('card-account::credit-card-new-account.glossary.account')}
                    </TextCaption>
                    <H6 sx={textAtmPin}>
                      {data.assetLink.name} ({data.assetLink.accountNumber})
                    </H6>
                  </View>
                )}
                {data.accountType == 'fixed-limit' && data.creditAccount && (
                  <View>
                    <TextCaption sx={textTitle} semiBold>
                      {t('card-account::credit-card-new-account.glossary.credit-account')}
                    </TextCaption>
                    <H6 sx={textAtmPin}>
                      {data.creditAccount.name} ({data.creditAccount.accountNumber})
                    </H6>
                  </View>
                )}
                {data.accountType == 'fixed-limit' && data.limit && (
                  <View>
                    <TextCaption sx={textTitle} semiBold>
                      {t('card-account::credit-card-new-account.glossary.limit')}
                    </TextCaption>
                    <H6 sx={textAtmPin}>
                      {formatAmountByDecimal({ value: data.limit, decimals: 2 })} {data.currency}
                    </H6>
                  </View>
                )}
                {data.accountType == 'fixed-limit' && data.settlementAccount && (
                  <View>
                    <TextCaption sx={textTitle} semiBold>
                      {t('card-account::credit-card-new-account.glossary.settlement-account')}
                    </TextCaption>
                    <H6 sx={textAtmPin}>
                      {data.settlementAccount.name} ({data.settlementAccount.accountNumber})
                    </H6>
                  </View>
                )}
                {data.individual && data?.identityType == 'individual' && (
                  <View>
                    <TextCaption sx={textTitle} semiBold>
                      {t('card-account::credit-card-new-account.glossary.account-holder')}
                    </TextCaption>
                    <H6 sx={textAtmPin}>{`${data.individual?.firstName} ${data.individual?.lastName}`}</H6>
                  </View>
                )}
                {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
                <View sx={btnView}>
                  <UniButton
                    sx={btn}
                    labelSx={{ color: '$balck' }}
                    label={t('card-account-management::general.button--place-instruction')}
                    loading={loading}
                    disabled={loading}
                    onPress={() => {
                      setLoading(true);
                      func &&
                        func(
                          submitData,
                          (returnData) => {
                            setLoading(false);
                            onReturnData && onReturnData(returnData.data);
                            navigation.navigate(CreateCardAccountStackRoutes.Placed, {
                              onLayout: onLayout,
                              data,
                              returnData: returnData.data,
                              refresh
                            });
                          },
                          (error) => {
                            setLoading(false);
                            const data: any = error.response?.data;
                            const errorMessage =
                              data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                            setAlertMessage(errorMessage);
                          }
                        );
                    }}
                  />
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {
    gap: '$5'
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
